import { Flex, Progress, Text } from "@chakra-ui/react";
import { useAppNetwork } from "contexts/OfflineContext";
import useGetCurrentPath from "hooks/useGetCurrentPath";
import useReduxState from "hooks/useReduxState";
import { isEqual } from "lodash-es";
import { memo, useEffect, useMemo } from "react";
import CacheManager from "libs/cache-project/classes/CacheManager";
import { useCacheManagerStore } from "libs/cache-project/hooks/useCacheManagerStore";
import {
  CacheProjectState,
  CacheProjectStatus,
} from "libs/cache-project/types";
import { useDispatch } from "react-redux";
import { setIsShowOfflineMsg } from "redux/appSlice";
import { RootState } from "redux/store";
import { routePath } from "routes/path";
import { getHumanReadableSize } from "utils/storage";
import { message } from "./base";
import { NetworkStatusIcon, SyncDataForOfflineIcon } from "./icon";
import { formatDate } from "utils/date";

interface Props {
  isShowSyncDataForOfflineIcon?: boolean;
}

const OfflineBar = (props: Props) => {
  const { isShowSyncDataForOfflineIcon = true } = props;

  const { dataProjectDetail, dataProjects } = useReduxState(
    (state: RootState) => ({
      dataProjectDetail: state.project.dataProjectDetail,
      dataProjects: state.project.dataProjects,
    }),
    isEqual
  );

  const isSyncOfflineData = useReduxState(
    (state: RootState) => state.app.isSyncOfflineData
  );

  const isLoadedViewerModelData = useReduxState(
    (state: RootState) => state.forgeViewer.isLoadedViewerModelData
  );

  const { isOnline, timeBeginOffline, offlineEditedDataSize } = useAppNetwork();
  const { pathPattern } = useGetCurrentPath();
  const dispatch = useDispatch();

  const isPageForgeViewer = useMemo(() => {
    return [routePath.ForgeViewer]?.includes(pathPattern);
  }, [pathPattern]);

  const mapProjectState = useCacheManagerStore(
    (state) => state.mapProjectState || {}
  );

  const cacheProjectState: CacheProjectState | undefined = useMemo(
    () =>
      dataProjectDetail?.id ? mapProjectState[dataProjectDetail.id] : undefined,
    [mapProjectState, dataProjectDetail?.id]
  );

  const uploadOfflineDataStatus = useMemo(() => {
    if (!offlineEditedDataSize) {
      return <></>;
    }

    if (!isOnline) {
      return <Text>{getHumanReadableSize(offlineEditedDataSize)}</Text>;
    }

    if (isSyncOfflineData && offlineEditedDataSize) {
      return (
        <Flex flexDir="column" pt="5px">
          <Text userSelect="none">
            {getHumanReadableSize(offlineEditedDataSize)}
          </Text>
          <Progress
            size="sm"
            w="100%"
            bgColor={"var(--chakra-colors-gray-300)"}
            colorScheme={"completed"}
            hasStripe={true}
            isAnimated={true}
            isIndeterminate
            border="5px"
          />
        </Flex>
      );
    }

    return <></>;
  }, [isOnline, isSyncOfflineData, offlineEditedDataSize]);

  useEffect(() => {
    if (!isOnline && isPageForgeViewer) {
      message.warning([
        "インターネット接続が切れました。",
        "オフライン環境で利用したい場合には、オフラインモードをご活用ください。（オフラインモードを利用する際には、事前にプロジェクトデータのダウンロードが必要です）",
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  useEffect(() => {
    // Do not show offline messages when in the forge viewer screen without caching the project
    if (isPageForgeViewer) {
      dispatch(setIsShowOfflineMsg((cacheProjectState?.progress || 0) >= 100));
    }

    return () => {
      dispatch(setIsShowOfflineMsg(true));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageForgeViewer, (cacheProjectState?.progress || 0) >= 100]);

  // This useMemo hook computes a formatted timestamp string that shows when projects were last downloaded or updated
  const timeDownloadProject = useMemo(() => {
    const projects = Object.values(mapProjectState);
    if (!projects?.length) {
      return undefined;
    }

    const formatProjectTimestamp = (
      project: CacheProjectState | undefined,
      includeProjectName = false
    ) => {
      if (!project?.lastUpdated) {
        return undefined;
      }

      const timestamp = formatDate(project.lastUpdated, "YYYY/MM/DD HH:mm");
      const projectName =
        dataProjects.find((item) => item.id === project.projectId)?.name || "";

      if (!includeProjectName) {
        return `確認！\n${timestamp}にダウンロードしたデータを元にオフライン作業を実施しています。`;
      }

      return `・${timestamp}にダウンロードした${projectName}のデータ`;
    };

    if (!dataProjectDetail?.id) {
      // Get all downloaded projects
      const downloadedProjects = projects
        .filter(
          (project) =>
            project.lastUpdated &&
            project.status === CacheProjectStatus.FINISHED
        )
        .sort((a, b) => (b.lastUpdated || 0) - (a.lastUpdated || 0))
        .map((project) => formatProjectTimestamp(project, true))
        .filter(Boolean);

      if (downloadedProjects.length === 0) {
        return undefined;
      }

      return `確認！\n${downloadedProjects.join(
        "\n"
      )}\nを元に、オフライン作業を実施しています。`;
    }

    const selectedProject = projects.find(
      (item) => item.projectId === dataProjectDetail?.id
    );

    return formatProjectTimestamp(selectedProject);
  }, [dataProjectDetail?.id, dataProjects, mapProjectState]);

  return (
    <>
      {uploadOfflineDataStatus}
      {isShowSyncDataForOfflineIcon && (
        <SyncDataForOfflineIcon
          isOnline={isOnline}
          cachingBimFileId={cacheProjectState?.projectId || ""}
          isPageForgeViewer={isPageForgeViewer}
          bimfileId={dataProjectDetail?.id || ""}
          cacheProjectState={cacheProjectState}
          stopCacheProject={() => CacheManager.pause(dataProjectDetail?.id!)}
          syncNewProjectData={() => CacheManager.start(dataProjectDetail?.id!)}
        />
      )}
      <NetworkStatusIcon
        isOnline={isOnline}
        isCacheFinished={
          cacheProjectState?.status === CacheProjectStatus.FINISHED
        }
        isLoadingViewer={isPageForgeViewer && !isLoadedViewerModelData}
        isCaching={cacheProjectState?.status === CacheProjectStatus.PROCESSING}
        timeBeginOffline={timeBeginOffline}
        timeDownloadProject={timeDownloadProject}
        isPageForgeViewer={isPageForgeViewer}
      />
    </>
  );
};

export default memo(OfflineBar);
